import isNil from 'lodash/isNil';
import { useEffect, useRef } from 'react';

import userDeviceTokenClient from 'clients/userDeviceToken';
import {
  NativeDeviceToken,
  getNativeDeviceToken,
  subscribeToNativeDeviceToken
} from 'utils/native-container/deviceToken';

/**
 * Register the handset's push token against the signed-in user.
 *
 * Registration needs two things that arrive independently and in no fixed
 * order: the user id, which resolves when Auth0 does, and the device token,
 * which the container injects when the OS hands it over. Either can be last, so
 * this watches both and calls once both are present.
 *
 * A no-op in a normal browser, where there is no container and no token.
 *
 * @param userId the signed-in user, or undefined while nobody is
 */
export default function useNativeDeviceTokenRegistration(userId: number | undefined): void {
  /**
   * The pairing already registered, as `<userId>:<deviceToken>`.
   *
   * The container re-injects on every page load, and the platform reloads on
   * every unlock, retry and crash recovery, so without this a farmer unlocking
   * their phone re-registers on each one.
   *
   * Cleared when a call fails, so the next injection retries. That is the whole
   * retry strategy, and it is enough: injections are frequent, registration is
   * idempotent, and a device that never registers is a phone that silently
   * stops receiving alerts, which is worth another attempt but not worth
   * blocking anything over.
   */
  const registeredRef = useRef<string | undefined>();

  useEffect(() => {
    if (isNil(userId)) {
      // Deregistration is not done here. `logoutAnnouncingToNative` owns it,
      // because Auth0 replaces the document immediately on logout and nothing
      // scheduled from this effect would survive to make the call.
      registeredRef.current = undefined;
      return undefined;
    }

    let cancelled = false;

    const register = async (device: NativeDeviceToken) => {
      const pairing = `${userId}:${device.deviceToken}`;
      if (registeredRef.current === pairing) return;
      registeredRef.current = pairing;

      const { error } = await userDeviceTokenClient.registerDeviceToken(device);
      if (cancelled) return;
      if (error) registeredRef.current = undefined;
    };

    // Subscribe before reading, so a token injected between the two is not
    // missed. A duplicate is harmless; the pairing check absorbs it.
    const unsubscribe = subscribeToNativeDeviceToken(device => {
      void register(device);
    });

    const existing = getNativeDeviceToken();
    if (existing) void register(existing);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);
}
